import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { DossierPaiementMode } from '@/hooks/queries/usePaiementsFormateurs';

// Sprint 6.9.2 — Consolidé dettes fournisseurs B2B
// Table créée par migration 102 (dettes_fournisseurs).

export type DetteFournisseurStatut = 'a_payer' | 'partiel' | 'paye';

export interface DetteFournisseurRow {
  id: string;
  entreprise_id: string;
  activite_id: string | null;
  fournisseur_tier_id: string | null;
  fournisseur_nom: string;
  fournisseur_telephone: string | null;
  fournisseur_rib: string | null;
  fournisseur_email: string | null;
  annee: number;
  formation_id: string | null;
  formation_titre: string | null;
  date_formation: string | null;
  reference_facture: string | null;
  montant_dette: number;
  montant_regle: number;
  statut: DetteFournisseurStatut;
  mode_paiement: DossierPaiementMode | null;
  reference_operation: string | null;
  date_virement: string | null;
  observation: string | null;
  operation_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface DettesFournisseursFilter {
  entrepriseId: string | null;
  annee?: number | null;
  statut?: DetteFournisseurStatut | null;
  fournisseurTierId?: string | null;
  search?: string;
}

export function useDettesFournisseurs(filter: DettesFournisseursFilter) {
  return useQuery({
    queryKey: ['dettes_fournisseurs', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<DetteFournisseurRow[]> => {
      let q = supabase
        .from('dettes_fournisseurs')
        .select('*')
        .order('annee', { ascending: false })
        .order('date_formation', { ascending: false, nullsFirst: false })
        .limit(2000);
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.annee) q = q.eq('annee', filter.annee);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.fournisseurTierId) q = q.eq('fournisseur_tier_id', filter.fournisseurTierId);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(
          `fournisseur_nom.ilike.%${s}%,formation_titre.ilike.%${s}%,reference_facture.ilike.%${s}%,observation.ilike.%${s}%`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as DetteFournisseurRow[];
    },
  });
}

// =====================================================================
// Helpers parsing import (DZD-safe — Sprint 6.9.1.2)
// =====================================================================

const STATUT_FROM_EXCEL: Record<string, DetteFournisseurStatut> = {
  regle: 'paye',
  réglé: 'paye',
  paye: 'paye',
  payé: 'paye',
  payee: 'paye',
  payée: 'paye',
  dette_non_reglee: 'a_payer',
  'dette non reglee': 'a_payer',
  'dette non réglée': 'a_payer',
  non_regle: 'a_payer',
  'non réglé': 'a_payer',
  'non regle': 'a_payer',
  a_payer: 'a_payer',
  partiel: 'partiel',
  partielle: 'partiel',
};

const MODE_FROM_EXCEL: Record<string, DossierPaiementMode> = {
  virement: 'virement',
  cheque: 'cheque',
  chèque: 'cheque',
  especes: 'especes',
  espèces: 'especes',
  cash: 'especes',
  ccp: 'ccp',
  carte: 'carte',
};

function normalizeKey(s: string | null | undefined): string {
  return (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseStatut(raw: string | null): DetteFournisseurStatut {
  const k = normalizeKey(raw);
  return STATUT_FROM_EXCEL[k] ?? 'a_payer';
}

function parseMode(raw: string | null): DossierPaiementMode | null {
  const k = normalizeKey(raw);
  return MODE_FROM_EXCEL[k] ?? null;
}

function parseDate(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.toString().trim();
  if (!s) return null;
  const m1 = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`;
  const m2 = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/.exec(s);
  if (m2) {
    const [, d, mo, yRaw] = m2;
    const y = (yRaw ?? '').length === 2 ? `20${yRaw}` : (yRaw ?? '');
    return `${y}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

function parseNumber(raw: string | number | null): number {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return raw;
  const cleaned = raw.toString().replace(/[^\d-]/g, '');
  if (cleaned === '' || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

// Calcule le statut selon montant_regle vs montant_dette
function computeStatut(montant_dette: number, montant_regle: number): DetteFournisseurStatut {
  if (montant_regle <= 0) return 'a_payer';
  if (montant_regle >= montant_dette) return 'paye';
  return 'partiel';
}

// =====================================================================
// IMPORT — cascade auto fournisseurs + formations + dettes
// =====================================================================

export interface ImportDetteRow {
  fournisseur: string | null;
  telephone: string | null;
  rib: string | null;
  email: string | null;
  annee: string | number | null;
  formation: string | null;
  date_formation: string | null;
  reference_facture: string | null;
  montant_dette: string | number | null;
  montant_regle: string | number | null;
  statut: string | null;
  mode_paiement: string | null;
  reference_operation: string | null;
  date_virement: string | null;
  observation: string | null;
}

export interface ImportDettesInput {
  entrepriseId: string;
  activiteId: string | null;
  rows: ImportDetteRow[];
}

export interface ImportDettesResult {
  dettesCrees: number;
  fournisseursCrees: number;
  formationsCreees: number;
  errors: string[];
}

export function useImportDettesFournisseurs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportDettesInput): Promise<ImportDettesResult> => {
      const result: ImportDettesResult = {
        dettesCrees: 0,
        fournisseursCrees: 0,
        formationsCreees: 0,
        errors: [],
      };

      const { data: existingTiers = [] } = await supabase
        .from('tiers')
        .select('id, raison_sociale, telephone, rib, email')
        .eq('entreprise_id', input.entrepriseId)
        .eq('type', 'fournisseur');
      const tierByKey = new Map<
        string,
        { id: string; rib: string | null; telephone: string | null; email: string | null }
      >();
      for (const t of existingTiers as {
        id: string;
        raison_sociale: string;
        rib: string | null;
        telephone: string | null;
        email: string | null;
      }[]) {
        tierByKey.set(normalizeKey(t.raison_sociale), {
          id: t.id,
          rib: t.rib,
          telephone: t.telephone,
          email: t.email,
        });
      }

      const { data: existingFormations = [] } = await supabase
        .from('formations')
        .select('id, titre, code')
        .eq('entreprise_id', input.entrepriseId);
      const formationByKey = new Map<string, string>();
      for (const f of existingFormations as { id: string; titre: string }[]) {
        formationByKey.set(normalizeKey(f.titre), f.id);
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      for (let i = 0; i < input.rows.length; i++) {
        const row = input.rows[i];
        if (!row) continue;
        const lineNo = i + 2;
        try {
          const fournisseurNom = (row.fournisseur ?? '').toString().trim();
          if (!fournisseurNom) {
            result.errors.push(`L${lineNo}: nom fournisseur manquant`);
            continue;
          }
          const annee = parseNumber(row.annee) || new Date().getFullYear();
          const montant_dette = parseNumber(row.montant_dette);
          const montant_regle = parseNumber(row.montant_regle);
          if (montant_dette < 0 || montant_regle < 0) {
            result.errors.push(`L${lineNo}: montants négatifs interdits`);
            continue;
          }

          // 1. Tier fournisseur — find or create
          const tierKey = normalizeKey(fournisseurNom);
          let fournisseurTierId = tierByKey.get(tierKey)?.id ?? null;
          if (!fournisseurTierId) {
            const { data: created, error: errTier } = await supabase
              .from('tiers')
              .insert({
                entreprise_id: input.entrepriseId,
                type: 'fournisseur',
                raison_sociale: fournisseurNom,
                telephone: row.telephone ?? null,
                rib: row.rib ?? null,
                email: row.email ?? null,
                activite_principale_id: input.activiteId,
              } as never)
              .select('id')
              .single();
            if (errTier) {
              result.errors.push(`L${lineNo}: tier fournisseur KO — ${errTier.message}`);
              continue;
            }
            fournisseurTierId = (created as { id: string } | null)?.id ?? null;
            if (fournisseurTierId) {
              tierByKey.set(tierKey, {
                id: fournisseurTierId,
                rib: row.rib ?? null,
                telephone: row.telephone ?? null,
                email: row.email ?? null,
              });
              result.fournisseursCrees++;
            }
          }

          // 2. Formation — find or create (optionnel)
          let formationId: string | null = null;
          const formationTitre = (row.formation ?? '').toString().trim();
          if (formationTitre) {
            const formKey = normalizeKey(formationTitre);
            formationId = formationByKey.get(formKey) ?? null;
            if (!formationId) {
              const code = `IMP-${Math.floor(Math.random() * 9000 + 1000)}`;
              const { data: createdF, error: errF } = await supabase
                .from('formations')
                .insert({
                  entreprise_id: input.entrepriseId,
                  code,
                  titre: formationTitre,
                } as never)
                .select('id')
                .single();
              if (errF) {
                result.errors.push(`L${lineNo}: formation KO — ${errF.message}`);
              } else {
                formationId = (createdF as { id: string } | null)?.id ?? null;
                if (formationId) {
                  formationByKey.set(formKey, formationId);
                  result.formationsCreees++;
                }
              }
            }
          }

          // 3. Statut : priorité Excel, sinon dérivé du ratio
          const statutExcel = parseStatut(row.statut);
          const statut =
            row.statut && row.statut.toString().trim() !== ''
              ? statutExcel
              : computeStatut(montant_dette, montant_regle);

          // 4. INSERT dette
          const { error: errIns } = await supabase.from('dettes_fournisseurs').insert({
            entreprise_id: input.entrepriseId,
            activite_id: input.activiteId,
            fournisseur_tier_id: fournisseurTierId,
            fournisseur_nom: fournisseurNom,
            fournisseur_telephone: row.telephone ?? null,
            fournisseur_rib: row.rib ?? null,
            fournisseur_email: row.email ?? null,
            annee,
            formation_id: formationId,
            formation_titre: formationTitre || null,
            date_formation: parseDate(row.date_formation),
            reference_facture: row.reference_facture ?? null,
            montant_dette,
            montant_regle,
            statut,
            mode_paiement: parseMode(row.mode_paiement),
            reference_operation: row.reference_operation ?? null,
            date_virement: parseDate(row.date_virement),
            observation: row.observation ?? null,
            created_by: createdBy,
          } as never);
          if (errIns) {
            result.errors.push(`L${lineNo}: insert dette KO — ${errIns.message}`);
            continue;
          }
          result.dettesCrees++;
        } catch (e) {
          result.errors.push(`L${lineNo}: ${(e as Error).message}`);
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dettes_fournisseurs'] });
      void qc.invalidateQueries({ queryKey: ['tiers'] });
      void qc.invalidateQueries({ queryKey: ['formations'] });
    },
  });
}

// =====================================================================
// CREATE manuel
// =====================================================================

export interface CreateDetteInput {
  entreprise_id: string;
  activite_id: string | null;
  fournisseur_tier_id: string | null;
  fournisseur_nom: string;
  fournisseur_telephone: string | null;
  fournisseur_rib: string | null;
  fournisseur_email: string | null;
  annee: number;
  formation_id: string | null;
  formation_titre: string | null;
  date_formation: string | null;
  reference_facture: string | null;
  montant_dette: number;
  montant_regle: number;
  statut: DetteFournisseurStatut;
  mode_paiement: DossierPaiementMode | null;
  reference_operation: string | null;
  date_virement: string | null;
  observation: string | null;
}

export function useCreateDette() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDetteInput): Promise<DetteFournisseurRow> => {
      const { data, error } = await supabase
        .from('dettes_fournisseurs')
        .insert(input as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as DetteFournisseurRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dettes_fournisseurs'] });
    },
  });
}

// =====================================================================
// UPDATE manuel
// =====================================================================

export interface UpdateDetteInput {
  id: string;
  annee?: number;
  fournisseur_tier_id?: string | null;
  fournisseur_nom?: string;
  fournisseur_telephone?: string | null;
  fournisseur_rib?: string | null;
  fournisseur_email?: string | null;
  formation_id?: string | null;
  formation_titre?: string | null;
  date_formation?: string | null;
  reference_facture?: string | null;
  montant_dette?: number;
  montant_regle?: number;
  statut?: DetteFournisseurStatut;
  mode_paiement?: DossierPaiementMode | null;
  reference_operation?: string | null;
  date_virement?: string | null;
  observation?: string | null;
}

export function useUpdateDette() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateDetteInput): Promise<void> => {
      const { id, ...rest } = input;
      const { error } = await supabase
        .from('dettes_fournisseurs')
        .update(rest as never)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dettes_fournisseurs'] });
    },
  });
}

// =====================================================================
// DELETE — cascade opération liée
// =====================================================================

export function useDeleteDette() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dette: { id: string; operation_id: string | null }): Promise<void> => {
      if (dette.operation_id) {
        const { error: errOp } = await supabase
          .from('operations')
          .delete()
          .eq('id', dette.operation_id);
        if (errOp && !/no rows/i.test(errOp.message)) {
          throw new Error(`Suppression opération Caisse KO : ${errOp.message}`);
        }
      }
      const { error } = await supabase.from('dettes_fournisseurs').delete().eq('id', dette.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dettes_fournisseurs'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// ENREGISTRER PAIEMENT (partiel ou full) — cross-ref operation SORTIE
// =====================================================================

export interface EnregistrerPaiementDetteInput {
  dette: DetteFournisseurRow;
  montantPaye: number;
  dateReglement: string;
  modePaiement: DossierPaiementMode;
  reference: string | null;
  caisseId: string | null;
  compteBancaireId: string | null;
}

export function useEnregistrerPaiementDette() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: EnregistrerPaiementDetteInput,
    ): Promise<{ operation_id: string; nouveau_statut: DetteFournisseurStatut }> => {
      const {
        dette,
        montantPaye,
        dateReglement,
        modePaiement,
        reference,
        caisseId,
        compteBancaireId,
      } = input;

      if (montantPaye <= 0) throw new Error('Montant à régler > 0 obligatoire');
      const reste = dette.montant_dette - dette.montant_regle;
      if (montantPaye > reste + 0.01) {
        throw new Error(`Montant trop élevé : reste à payer ${reste.toLocaleString('fr-DZ')} DA`);
      }

      let canal: 'especes' | 'ccp' | 'banque';
      if (modePaiement === 'especes') canal = 'especes';
      else if (modePaiement === 'ccp') canal = 'ccp';
      else canal = 'banque';

      if (canal === 'especes' && !caisseId) {
        throw new Error('Caisse obligatoire pour un paiement en espèces');
      }
      if (canal !== 'especes' && !compteBancaireId) {
        throw new Error('Compte bancaire obligatoire pour ce mode de paiement');
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const reference_op =
        reference ?? `DETTE-${dette.annee}-${Math.floor(Math.random() * 9000 + 1000)}`;
      const motif = `Règlement dette fournisseur — ${dette.fournisseur_nom}${dette.reference_facture ? ` · ${dette.reference_facture}` : ''}`;

      const { data: opCreated, error: errOp } = await supabase
        .from('operations')
        .insert({
          entreprise_id: dette.entreprise_id,
          reference: reference_op,
          date_operation: `${dateReglement}T12:00:00`,
          sens: 'sortie',
          canal,
          categorie: 'sous_traitance',
          caisse_id: canal === 'especes' ? caisseId : null,
          compte_bancaire_id: canal !== 'especes' ? compteBancaireId : null,
          tier_id: dette.fournisseur_tier_id,
          activite_id: dette.activite_id,
          activite_beneficiaire_id: dette.activite_id,
          activite_emettrice_id: dette.activite_id,
          devise_code: 'DZD',
          montant: montantPaye,
          taux_change: 1,
          montant_dzd: montantPaye,
          motif,
          observation: `Dette ${dette.id.slice(0, 8)} — ${dette.montant_regle + montantPaye >= dette.montant_dette ? 'soldée' : 'paiement partiel'}`,
          statut: 'validee',
          created_by: user?.id ?? null,
        } as never)
        .select('id')
        .single();
      if (errOp) throw new Error(`Opération KO : ${errOp.message}`);
      const operationId = (opCreated as { id: string } | null)?.id;
      if (!operationId) throw new Error('Opération créée sans id');

      const nouveauRegle = dette.montant_regle + montantPaye;
      const nouveauStatut: DetteFournisseurStatut =
        nouveauRegle >= dette.montant_dette ? 'paye' : 'partiel';

      const { error: errUpd } = await supabase
        .from('dettes_fournisseurs')
        .update({
          montant_regle: nouveauRegle,
          statut: nouveauStatut,
          mode_paiement: modePaiement,
          reference_operation: reference_op,
          date_virement: dateReglement,
          // Lien op uniquement quand soldée (pour cascade delete propre)
          operation_id: nouveauStatut === 'paye' ? operationId : dette.operation_id,
        } as never)
        .eq('id', dette.id);
      if (errUpd) throw new Error(`Update dette KO : ${errUpd.message}`);

      return { operation_id: operationId, nouveau_statut: nouveauStatut };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dettes_fournisseurs'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// BULK — marquer payées en lot (1 modal global)
// =====================================================================

export interface BulkMarquerDettesPayeesInput {
  dettes: DetteFournisseurRow[];
  dateReglement: string;
  modePaiement: DossierPaiementMode;
  caisseId: string | null;
  compteBancaireId: string | null;
}

export interface BulkMarquerDettesPayeesResult {
  success: number;
  errors: { dette_id: string; message: string }[];
}

export function useBulkMarquerDettesPayees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: BulkMarquerDettesPayeesInput,
    ): Promise<BulkMarquerDettesPayeesResult> => {
      const result: BulkMarquerDettesPayeesResult = { success: 0, errors: [] };
      let canal: 'especes' | 'ccp' | 'banque';
      if (input.modePaiement === 'especes') canal = 'especes';
      else if (input.modePaiement === 'ccp') canal = 'ccp';
      else canal = 'banque';
      if (canal === 'especes' && !input.caisseId) {
        throw new Error('Caisse obligatoire pour un paiement en espèces');
      }
      if (canal !== 'especes' && !input.compteBancaireId) {
        throw new Error('Compte bancaire obligatoire pour ce mode de paiement');
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      for (const dette of input.dettes) {
        if (dette.statut === 'paye') continue;
        const reste = dette.montant_dette - dette.montant_regle;
        if (reste <= 0) continue;
        try {
          const ref = `DETTE-${dette.annee}-${Math.floor(Math.random() * 9000 + 1000)}`;
          const motif = `Règlement dette fournisseur — ${dette.fournisseur_nom}${dette.reference_facture ? ` · ${dette.reference_facture}` : ''}`;
          const { data: opCreated, error: errOp } = await supabase
            .from('operations')
            .insert({
              entreprise_id: dette.entreprise_id,
              reference: ref,
              date_operation: `${input.dateReglement}T12:00:00`,
              sens: 'sortie',
              canal,
              categorie: 'sous_traitance',
              caisse_id: canal === 'especes' ? input.caisseId : null,
              compte_bancaire_id: canal !== 'especes' ? input.compteBancaireId : null,
              tier_id: dette.fournisseur_tier_id,
              activite_id: dette.activite_id,
              activite_beneficiaire_id: dette.activite_id,
              activite_emettrice_id: dette.activite_id,
              devise_code: 'DZD',
              montant: reste,
              taux_change: 1,
              montant_dzd: reste,
              motif,
              observation: `Bulk paiement — dette ${dette.id.slice(0, 8)} soldée`,
              statut: 'validee',
              created_by: createdBy,
            } as never)
            .select('id')
            .single();
          if (errOp) {
            result.errors.push({ dette_id: dette.id, message: errOp.message });
            continue;
          }
          const operationId = (opCreated as { id: string } | null)?.id;
          if (!operationId) {
            result.errors.push({ dette_id: dette.id, message: 'Op créée sans id' });
            continue;
          }
          const { error: errUpd } = await supabase
            .from('dettes_fournisseurs')
            .update({
              montant_regle: dette.montant_dette,
              statut: 'paye',
              mode_paiement: input.modePaiement,
              reference_operation: ref,
              date_virement: input.dateReglement,
              operation_id: operationId,
            } as never)
            .eq('id', dette.id);
          if (errUpd) {
            result.errors.push({ dette_id: dette.id, message: errUpd.message });
            continue;
          }
          result.success++;
        } catch (e) {
          result.errors.push({ dette_id: dette.id, message: (e as Error).message });
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dettes_fournisseurs'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}
